import React, { useEffect, useState } from "react";
import { Edit, Trash2 } from "lucide-react";
import { couponService } from "../../services/couponService";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { CouponForm } from "./CouponForm";
import { toast } from "react-toastify";

const CouponList = ({ categories = [] }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async (page = 1, loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await couponService.getCoupons(page);

      if (response?.results) {
        setCoupons((prevCoupons) =>
          loadMore ? [...prevCoupons, ...response.results] : response.results
        );
        setCurrentPage(response.currentPage || 1);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch coupons:", error);
      toast.error("Failed to load coupons!");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleDeleteCoupon = async () => {
    if (!selectedCoupon?._id) return;

    try {
      await couponService.deleteCoupon(selectedCoupon._id);
      setCoupons((prev) =>
        prev.filter((coupon) => coupon._id !== selectedCoupon._id)
      );
      setIsDeleteModalOpen(false);
      toast.success("Coupon deleted successfully!");
    } catch (error) {
      console.error("Failed to delete coupon:", error);
      toast.error("Failed to delete coupon!");
    }
  };

  const handleEditCoupon = async (formData) => {
    if (!selectedCoupon?._id) return;

    try {
      const updatedCoupon = await couponService.updateCoupon(
        selectedCoupon._id,
        formData
      );
      if (updatedCoupon) {
        setCoupons((prev) =>
          prev.map((coupon) =>
            coupon._id === updatedCoupon._id ? updatedCoupon : coupon
          )
        );
        setIsEditModalOpen(false);
        toast.success("Coupon updated successfully!");
      }
    } catch (error) {
      console.error("Failed to update coupon:", error);
      toast.error("Failed to update coupon!");
    }
  };

  const loadMoreCoupons = () => {
    if (currentPage < totalPages) {
      fetchCoupons(currentPage + 1, true);
    }
  };

  if (loading && !coupons.length) {
    return <p className="text-center py-4">Loading...</p>;
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {coupons && coupons.length > 0 ? (
        coupons.map((coupon) => (
          <div
            key={coupon._id}
            className="flex items-center justify-between p-4 border-b hover:bg-gray-50 transition"
          >
            <div>
              <h3 className="text-lg font-semibold">{coupon.code}</h3>
              <p className="text-sm text-gray-500">
                Discount: {coupon.discount} {coupon.discountType === "percentage" ? "%" : "$"}
              </p>
              <p className="text-sm text-gray-500">
                Expires:{" "}
                {coupon.expires &&
                  new Date(coupon.expires).toLocaleString("en-GB", {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    hour12: true,
                  })}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setSelectedCoupon(coupon);
                  setIsEditModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700"
              >
                <Edit size={20} />
              </button>
              <button
                onClick={() => {
                  setSelectedCoupon(coupon);
                  setIsDeleteModalOpen(true);
                }}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        ))
      ) : (
        <p className="text-center py-4">No coupons found</p>
      )}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteCoupon}
        itemName="coupon"
      />

      {selectedCoupon && (
        <CouponForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCoupon(null);
          }}
          onSubmit={handleEditCoupon}
          initialData={selectedCoupon}
          categories={categories}
        />
      )}

      {currentPage < totalPages && (
        <div className="flex justify-center p-4">
          <button
            onClick={loadMoreCoupons}
            disabled={loadingMore}
            className={`px-4 py-2 rounded-lg ${
              loadingMore
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
};

export default CouponList;
